import React, { useMemo, useRef, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Container,
    Divider,
    Flex,
    HStack,
    Heading,
    Icon,
    Link as ChakraLink,
    SimpleGrid,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VisuallyHidden,
    VStack,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
    FiAlertCircle,
    FiArrowRight,
    FiCheckCircle,
    FiClock,
    FiDownload,
    FiFileText,
    FiPrinter,
    FiUser,
    FiUsers,
} from 'react-icons/fi';
import Navbar from '../compnents/Navbar';
import Footer from '../compnents/Footer';
import useDocumentMeta from '../hooks/useDocumentMeta';
import { useTranslation, SUPPORTED_LANGUAGES } from '../i18n';
import { demoTrialSampleAssetPath } from '../content/demoDataset';
import { buildTrialHref } from './demoTrialHref';
import {
    demoAdvisers,
    demoBookSource,
    demoBookTotals,
    demoFirmProfile,
    demoReviewQueue,
    demoUrgencyPolicy,
    demoReferenceDate,
    formatDate,
    formatEur,
} from '../content/demoBookEs';

/**
 * Public demo. No login, no email, no data from the visitor.
 *
 * It exists because the product only produced value after a prospect uploaded
 * a real extract of their own client book — work and trust demanded before
 * anything was shown. Between the 30 September pricing release and
 * 21 October 2026 that funnel produced zero registrations. This page shows
 * the three screens the work happens on, on a synthetic Spanish book, and
 * only then asks for an account.
 *
 * Two things must stay true of every future edit:
 *   - the synthetic nature of the data is stated on the page, not in a
 *     footnote: a visitor must never be able to mistake it for a result;
 *   - no copy here claims an outcome, forecasts behaviour, or suggests the
 *     platform performs or replaces a suitability assessment.
 */

const URGENCY_STYLE = {
    high: { colorScheme: 'red' },
    medium: { colorScheme: 'orange' },
    low: { colorScheme: 'gray' },
    // A client whose evidence carries no date is not low priority: it is not
    // priced at all. Ranking one above a dated client would contradict the
    // rule the product is sold on, so it gets its own state and no score.
    verify: { colorScheme: 'blue' },
};

const PANEL = {
    bg: 'white',
    borderWidth: '1px',
    borderColor: 'brand.divider',
    borderRadius: '14px',
    boxShadow: 'b2bCard',
};

const SignalLine = ({ children }) => (
    <HStack align="flex-start" spacing={2}>
        <Icon as={FiCheckCircle} color="brand.tealDark" mt="3px" boxSize={3.5} />
        <Text fontSize="sm" color="brand.ink">{children}</Text>
    </HStack>
);

const MissingLine = ({ children }) => (
    <HStack align="flex-start" spacing={2}>
        <Icon as={FiAlertCircle} color="orange.500" mt="3px" boxSize={3.5} />
        <Text fontSize="sm" color="brand.ink">{children}</Text>
    </HStack>
);

const Fact = ({ label, value, muted = false }) => (
    <Flex justify="space-between" gap={4} py={1.5} borderBottomWidth="1px" borderColor="brand.divider">
        <Text fontSize="sm" color="brand.muted">{label}</Text>
        <Text fontSize="sm" fontWeight="700" color={muted ? 'orange.600' : 'brand.ink'} textAlign="right">
            {value}
        </Text>
    </Flex>
);

const DemoPage = () => {
    const { t, language, buildLanguageHref } = useTranslation();
    const { search } = useLocation();
    const trialHref = buildTrialHref(search, language);
    const [selectedId, setSelectedId] = useState(demoReviewQueue[0].clientId);
    const cardRef = useRef(null);
    const queueRef = useRef(null);

    useDocumentMeta({
        title: t('demo.meta.title'),
        description: t('demo.meta.description'),
        canonical: 'https://playground.finansee.eu/demo',
    });

    const client = useMemo(
        () => demoReviewQueue.find((row) => row.clientId === selectedId) || demoReviewQueue[0],
        [selectedId]
    );

    const adviserName = (adviserId) => {
        const adviser = demoAdvisers.find((item) => item.id === adviserId);
        return adviser ? t(adviser.nameKey) : t('common.notAvailable');
    };

    const signalText = (signal) => t(`demo.signal.${signal.code}`, signal);
    const missingText = (code) => t(`demo.missing.${code}`, {
        observed: formatDate(client.observedOn, language) || t('common.notAvailable'),
    });

    const openCard = (clientId) => {
        setSelectedId(clientId);
        // The card and the report both re-render for the chosen client, so the
        // visitor is moved to the card rather than left looking at the list.
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const observedLabel = formatDate(client.observedOn, language);
    const percent = (value) => t('demo.client.value.percent', { value });

    return (
        <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
            <Box className="demo-no-print">
                <Navbar />
            </Box>

            {/* Hero */}
            <Box className="demo-no-print grid-bg-dark quantum-mesh" bg="brand.quantumBg">
                <Container maxW="container.xl" py={{ base: 10, md: 14 }} position="relative" zIndex={1}>
                    <Flex justify="flex-end" mb={4}>
                        <HStack spacing={2} fontSize="sm">
                            <Text color="brand.quantumSubtle">{t('common.language')}:</Text>
                            {SUPPORTED_LANGUAGES.map((code) => (
                                <ChakraLink
                                    key={code}
                                    as={RouterLink}
                                    to={buildLanguageHref(code)}
                                    color={code === language ? 'brand.quantumAccent' : 'brand.quantumMuted'}
                                    fontWeight={code === language ? '800' : '600'}
                                    aria-current={code === language ? 'true' : undefined}
                                >
                                    {t(`common.language.${code}`)}
                                </ChakraLink>
                            ))}
                        </HStack>
                    </Flex>

                    <Badge
                        bg="rgba(0, 212, 170, 0.1)"
                        color="brand.quantumAccent"
                        borderRadius="full"
                        px={3}
                        py={1}
                        letterSpacing="0.15em"
                        textTransform="uppercase"
                    >
                        {t('demo.hero.badge')}
                    </Badge>
                    <Heading
                        as="h1"
                        mt={4}
                        color="white"
                        fontSize={{ base: '32px', md: '48px' }}
                        lineHeight="1.08"
                        maxW="4xl"
                    >
                        {t('demo.hero.title')}
                    </Heading>
                    <Text mt={4} color="brand.quantumMuted" fontSize="lg" maxW="3xl">
                        {t('demo.hero.subtitle', { clients: demoFirmProfile.clients })}
                    </Text>

                    <HStack mt={6} spacing={{ base: 3, md: 6 }} flexWrap="wrap">
                        {['noSignup', 'noData', 'oneMinute'].map((point) => (
                            <HStack key={point} spacing={2}>
                                <Icon as={FiCheckCircle} color="brand.quantumAccent" boxSize={4} />
                                <Text color="brand.quantumInk" fontSize="sm" fontWeight="700">
                                    {t(`demo.hero.point.${point}`)}
                                </Text>
                            </HStack>
                        ))}
                    </HStack>

                    <Button
                        mt={8}
                        size="lg"
                        rightIcon={<FiArrowRight />}
                        bgGradient="linear(135deg, brand.quantumAccent, brand.quantumCyan)"
                        color="brand.quantumBg"
                        _hover={{ opacity: 0.9 }}
                        onClick={() => queueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    >
                        {t('demo.hero.cta')}
                    </Button>
                </Container>
            </Box>

            {/* Synthetic-data disclosure. Deliberately before any figure. */}
            <Box bg="orange.50" borderBottomWidth="1px" borderColor="orange.200">
                <Container maxW="container.xl" py={4}>
                    <HStack align="flex-start" spacing={3}>
                        <Icon as={FiAlertCircle} color="orange.600" mt="3px" />
                        <Box>
                            <Text fontWeight="800" color="orange.800" fontSize="sm">
                                {t('demo.disclosure.title')}
                            </Text>
                            <Text color="orange.800" fontSize="sm" maxW="4xl">
                                {t('demo.disclosure.body')}
                            </Text>
                        </Box>
                    </HStack>
                </Container>
            </Box>

            <Container maxW="container.xl" py={{ base: 8, md: 12 }} flex="1">
                <VStack align="stretch" spacing={{ base: 8, md: 10 }}>
                    {/* The book */}
                    <Box {...PANEL} p={{ base: 5, md: 6 }} className="demo-no-print">
                        <HStack justify="space-between" flexWrap="wrap" gap={2}>
                            <Box>
                                <Heading as="h2" size="md" color="brand.navy">{t('demo.book.title')}</Heading>
                                <Text fontSize="sm" color="brand.muted" mt={1}>
                                    {t('demo.book.firm', {
                                        city: t(demoFirmProfile.cityKey),
                                        advisers: demoFirmProfile.advisers,
                                        analysts: demoFirmProfile.analysts,
                                    })}
                                </Text>
                            </Box>
                            <Badge colorScheme="orange">{t('common.syntheticBadge')}</Badge>
                        </HStack>

                        <SimpleGrid mt={5} columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
                            <Box>
                                <HStack spacing={2} color="brand.muted">
                                    <Icon as={FiUsers} boxSize={4} />
                                    <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" fontWeight="800">
                                        {t('demo.book.rows')}
                                    </Text>
                                </HStack>
                                <Text fontSize="2xl" fontWeight="800" color="brand.navy">
                                    {demoBookTotals.clients}
                                </Text>
                                <Text fontSize="xs" color="brand.subtle">
                                    {demoBookSource.fileName}
                                </Text>
                            </Box>
                            <Box>
                                <HStack spacing={2} color="brand.muted">
                                    <Icon as={FiFileText} boxSize={4} />
                                    <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" fontWeight="800">
                                        {t('demo.book.assets')}
                                    </Text>
                                </HStack>
                                <Text fontSize="2xl" fontWeight="800" color="brand.navy">
                                    {formatEur(demoFirmProfile.assetsEur, language)}
                                </Text>
                                <Text fontSize="xs" color="brand.subtle">
                                    {t('demo.book.median')}: {formatEur(demoFirmProfile.medianClientEur, language)}
                                </Text>
                            </Box>
                            <Box>
                                <HStack spacing={2} color="brand.muted">
                                    <Icon as={FiCheckCircle} boxSize={4} />
                                    <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" fontWeight="800">
                                        {t('demo.book.coverage')}
                                    </Text>
                                </HStack>
                                <Text fontSize="2xl" fontWeight="800" color="brand.navy">
                                    {Math.round(demoBookTotals.evidenceCoverage * 100)}%
                                </Text>
                                <Text fontSize="xs" color="brand.subtle">{t('demo.book.coverageHint')}</Text>
                            </Box>
                            <Box>
                                <HStack spacing={2} color="brand.muted">
                                    <Icon as={FiClock} boxSize={4} />
                                    <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" fontWeight="800">
                                        {t('demo.book.observed')}
                                    </Text>
                                </HStack>
                                <Text fontSize="2xl" fontWeight="800" color="brand.navy">
                                    {formatDate(demoBookSource.observedOn, language)}
                                </Text>
                                <Text fontSize="xs" color="brand.subtle">
                                    {t('demo.book.imported')}: {formatDate(demoBookSource.importedOn, language)}
                                </Text>
                            </Box>
                        </SimpleGrid>

                        <Divider my={5} />

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <MissingLine>
                                <Text as="span" fontWeight="700">{demoBookTotals.clientsWithoutSourceDate}</Text>
                                {' · '}{t('demo.book.undated')}. {t('demo.book.undatedHint')}
                            </MissingLine>
                            <MissingLine>
                                <Text as="span" fontWeight="700">{demoBookTotals.clientsNotRankable}</Text>
                                {' · '}{t('demo.book.notRankable')}. {t('demo.book.notRankableHint')}
                            </MissingLine>
                        </SimpleGrid>
                    </Box>

                    {/* 1. Review list */}
                    <Box ref={queueRef} {...PANEL} p={{ base: 4, md: 6 }} className="demo-no-print">
                        <Heading as="h2" size="md" color="brand.navy">{t('demo.queue.title')}</Heading>
                        <Text fontSize="sm" color="brand.muted" mt={1}>{t('demo.queue.subtitle')}</Text>
                        <Text fontSize="xs" color="brand.subtle" mt={1}>
                            {t('demo.queue.showing', { shown: demoReviewQueue.length, total: demoBookTotals.clients })}
                        </Text>

                        <Box mt={4} overflowX="auto">
                            <Table size="sm" variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th>{t('demo.queue.col.client')}</Th>
                                        <Th>{t('demo.queue.col.order')}</Th>
                                        <Th>{t('demo.queue.col.reason')}</Th>
                                        <Th>{t('demo.queue.col.evidence')}</Th>
                                        <Th>{t('demo.queue.col.observed')}</Th>
                                        <Th>{t('demo.queue.col.adviser')}</Th>
                                        {/* Named for screen readers: an empty
                                            header leaves the column's cells
                                            with nothing to announce them. */}
                                        <Th><VisuallyHidden>{t('demo.queue.open')}</VisuallyHidden></Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {demoReviewQueue.map((row) => (
                                        <Tr
                                            key={row.clientId}
                                            bg={row.clientId === selectedId ? 'brand.tealSoft' : undefined}
                                        >
                                            <Td>
                                                <Text fontWeight="700" color="brand.navy">{row.name}</Text>
                                                <Text fontSize="xs" color="brand.subtle" fontFamily="mono">
                                                    {row.clientId} · {row.country} · {formatEur(row.portfolioEur, language)}
                                                </Text>
                                            </Td>
                                            <Td>
                                                <HStack spacing={2}>
                                                    <Badge {...URGENCY_STYLE[row.urgency]}>
                                                        {t(`demo.urgency.${row.urgency}`)}
                                                    </Badge>
                                                    <Text fontSize="xs" color="brand.muted" fontWeight="700">
                                                        {row.priorityScore === null ? t('demo.priority.unscored') : `${row.priorityScore}/10`}
                                                    </Text>
                                                </HStack>
                                            </Td>
                                            <Td maxW="280px">
                                                <Text fontSize="sm" color="brand.ink">
                                                    {t(`demo.reason.${row.reason}`)}
                                                </Text>
                                            </Td>
                                            <Td>
                                                <Text fontSize="sm" color="brand.muted">
                                                    {t('demo.queue.coverage', {
                                                        value: Math.round(row.evidenceCoverage * 100),
                                                    })}
                                                </Text>
                                            </Td>
                                            <Td>
                                                {row.observedOn ? (
                                                    <Text fontSize="sm" color="brand.muted">
                                                        {formatDate(row.observedOn, language)}
                                                    </Text>
                                                ) : (
                                                    <Badge colorScheme="orange" variant="subtle">
                                                        {t('demo.queue.undated')}
                                                    </Badge>
                                                )}
                                            </Td>
                                            <Td>
                                                <Text fontSize="sm" color="brand.muted">{adviserName(row.adviserId)}</Text>
                                            </Td>
                                            <Td textAlign="right">
                                                <Button
                                                    size="xs"
                                                    colorScheme="teal"
                                                    variant={row.clientId === selectedId ? 'solid' : 'outline'}
                                                    onClick={() => openCard(row.clientId)}
                                                    aria-label={t('demo.queue.openAria', { name: row.name })}
                                                >
                                                    {t('demo.queue.open')}
                                                </Button>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>

                        <Box mt={5} bg="brand.sidebar" borderWidth="1px" borderColor="brand.divider" borderRadius="12px" p={4}>
                            <Heading as="h3" size="sm" color="brand.navy" mb={2}>{t('demo.guide.title')}</Heading>
                            <VStack align="stretch" spacing={1.5} fontSize="sm" color="brand.ink">
                                <Text>{t('demo.guide.order')}</Text>
                                <Text>
                                    {t('demo.guide.labels', {
                                        total: demoBookTotals.clients,
                                        ranked: demoBookTotals.clientsRanked,
                                        high: demoBookTotals.high,
                                        medium: demoBookTotals.medium,
                                        minimum: demoUrgencyPolicy.min_priority_score,
                                    })}
                                </Text>
                                <Text>{t('demo.guide.low', { notRankable: demoBookTotals.clientsNotRankable })}</Text>
                                <Text>{t('demo.guide.reference', { date: formatDate(demoReferenceDate, language) })}</Text>
                                <Text>{t('demo.guide.change')}</Text>
                                <Text fontWeight="700">{t('demo.guide.missing')}</Text>
                            </VStack>
                        </Box>
                    </Box>

                    {/* 2. Client card */}
                    <Box ref={cardRef} {...PANEL} p={{ base: 4, md: 6 }} className="demo-no-print">
                        <HStack justify="space-between" flexWrap="wrap" gap={2}>
                            <Box>
                                <Heading as="h2" size="md" color="brand.navy">{t('demo.client.title')}</Heading>
                                <Text fontSize="sm" color="brand.muted" mt={1}>
                                    {t('demo.client.subtitle', { name: client.name })}
                                </Text>
                            </Box>
                            <HStack spacing={2}>
                                <Badge {...URGENCY_STYLE[client.urgency]}>{t(`demo.urgency.${client.urgency}`)}</Badge>
                                <Badge colorScheme="orange">{t('common.syntheticBadge')}</Badge>
                            </HStack>
                        </HStack>

                        <SimpleGrid mt={5} columns={{ base: 1, lg: 3 }} spacing={6}>
                            <Box>
                                <HStack spacing={2} mb={2}>
                                    <Icon as={FiUser} color="brand.tealDark" />
                                    <Text fontWeight="800" color="brand.navy" fontSize="sm">
                                        {t('demo.client.facts')}
                                    </Text>
                                </HStack>
                                <Fact label={t('demo.client.field.id')} value={client.clientId} />
                                <Fact label={t('demo.client.field.country')} value={client.country} />
                                <Fact label={t('demo.client.field.age')} value={client.age} />
                                <Fact
                                    label={t('demo.client.field.risk')}
                                    value={t('demo.client.value.risk', { value: client.riskProfile })}
                                />
                                <Fact
                                    label={t('demo.client.field.knowledge')}
                                    value={client.knowledgeScore === null
                                        ? t('common.missing')
                                        : t('demo.client.value.knowledge', { value: client.knowledgeScore })}
                                    muted={client.knowledgeScore === null}
                                />
                                <Fact
                                    label={t('demo.client.field.experience')}
                                    value={client.experienceYears === null
                                        ? t('common.missing')
                                        : t('demo.client.value.years', { value: client.experienceYears })}
                                    muted={client.experienceYears === null}
                                />
                                <Fact
                                    label={t('demo.client.field.portfolio')}
                                    value={formatEur(client.portfolioEur, language)}
                                />
                                <Fact
                                    label={t('demo.client.field.home')}
                                    value={percent(client.homeWeightPct)}
                                />
                                <Fact
                                    label={t('demo.client.field.leverage')}
                                    value={client.leverage === null
                                        ? t('common.notAvailable')
                                        : t('demo.client.value.leverage', { value: client.leverage })}
                                    muted={client.leverage === null}
                                />
                                <Fact
                                    label={t('demo.client.field.observed')}
                                    value={observedLabel || t('demo.queue.undated')}
                                    muted={!observedLabel}
                                />
                                <Text fontSize="xs" color="brand.muted" mt={2}>
                                    {t('demo.client.field.allocation')}:{' '}
                                    {t('demo.client.value.allocation', {
                                        cash: client.cashPct,
                                        funds: client.fundsPct,
                                        stocks: client.singleStockPct,
                                    })}
                                </Text>
                            </Box>

                            <Box>
                                <Text fontWeight="800" color="brand.navy" fontSize="sm" mb={3}>
                                    {t('demo.client.signals')}
                                </Text>
                                <VStack align="stretch" spacing={2.5}>
                                    {client.signals.map((signal) => (
                                        <SignalLine key={signal.code}>{signalText(signal)}</SignalLine>
                                    ))}
                                    <Fact
                                        label={t('demo.client.field.coverage')}
                                        value={percent(Math.round(client.evidenceCoverage * 100))}
                                    />
                                </VStack>
                            </Box>

                            <Box>
                                <Text fontWeight="800" color="brand.navy" fontSize="sm" mb={3}>
                                    {t('demo.client.missing')}
                                </Text>
                                <VStack align="stretch" spacing={2.5}>
                                    {client.missing.length === 0 ? (
                                        <Text fontSize="sm" color="brand.muted">{t('demo.client.missingEmpty')}</Text>
                                    ) : (
                                        client.missing.map((code) => (
                                            <MissingLine key={code}>{missingText(code)}</MissingLine>
                                        ))
                                    )}
                                </VStack>

                                <Box mt={5} bg="brand.tealSoft" borderRadius="12px" p={4}>
                                    <Text fontSize="xs" fontWeight="800" color="brand.tealDark" textTransform="uppercase" letterSpacing="0.1em">
                                        {t('demo.client.nextStep')}
                                    </Text>
                                    <Text fontSize="sm" color="brand.ink" mt={2}>
                                        {t(`demo.nextStep.${client.nextStep}`)}
                                    </Text>
                                </Box>
                            </Box>
                        </SimpleGrid>

                        <Text fontSize="xs" color="brand.muted" mt={5}>
                            {t('demo.client.limits')}
                        </Text>
                    </Box>

                    {/* 3. Review report */}
                    <Box {...PANEL} p={{ base: 4, md: 6 }} className="demo-report">
                        <HStack justify="space-between" flexWrap="wrap" gap={2}>
                            <Box>
                                <Heading as="h2" size="md" color="brand.navy">{t('demo.report.title')}</Heading>
                                <Text fontSize="sm" color="brand.muted" mt={1}>
                                    {t('demo.report.subtitle', { name: client.name })}
                                </Text>
                            </Box>
                            <Button
                                className="demo-no-print"
                                size="sm"
                                variant="outline"
                                colorScheme="blue"
                                leftIcon={<FiPrinter />}
                                onClick={() => window.print()}
                            >
                                {t('demo.report.print')}
                            </Button>
                        </HStack>

                        <VStack align="stretch" spacing={5} mt={5}>
                            <Box>
                                <Heading as="h3" size="sm" color="brand.navy" mb={2}>
                                    {t('demo.report.section.sources')}
                                </Heading>
                                <Text fontSize="sm" color="brand.ink">
                                    {client.observedOn
                                        ? t('demo.report.sources.body', {
                                            file: demoBookSource.fileName,
                                            imported: formatDate(demoBookSource.importedOn, language),
                                            observed: observedLabel,
                                            rows: demoBookSource.rows,
                                            columns: demoBookSource.columns,
                                        })
                                        : t('demo.report.sources.undated', {
                                            file: demoBookSource.fileName,
                                            imported: formatDate(demoBookSource.importedOn, language),
                                        })}
                                </Text>
                            </Box>

                            <Box>
                                <Heading as="h3" size="sm" color="brand.navy" mb={2}>
                                    {t('demo.report.section.coverage')}
                                </Heading>
                                <Text fontSize="sm" color="brand.ink">
                                    {t('demo.report.coverage.body', {
                                        value: Math.round(client.evidenceCoverage * 100),
                                    })}
                                </Text>
                            </Box>

                            <Box>
                                <Heading as="h3" size="sm" color="brand.navy" mb={2}>
                                    {t('demo.report.section.indicators')}
                                </Heading>
                                <VStack align="stretch" spacing={2}>
                                    {client.signals.map((signal) => (
                                        <SignalLine key={`report-${signal.code}`}>{signalText(signal)}</SignalLine>
                                    ))}
                                </VStack>
                            </Box>

                            <Box>
                                <Heading as="h3" size="sm" color="brand.navy" mb={2}>
                                    {t('demo.report.section.verify')}
                                </Heading>
                                <VStack align="stretch" spacing={2}>
                                    {client.missing.length === 0 ? (
                                        <Text fontSize="sm" color="brand.ink">{t('demo.report.verify.empty')}</Text>
                                    ) : (
                                        client.missing.map((code) => (
                                            <MissingLine key={`report-${code}`}>{missingText(code)}</MissingLine>
                                        ))
                                    )}
                                    <SignalLine>{t(`demo.nextStep.${client.nextStep}`)}</SignalLine>
                                </VStack>
                            </Box>

                            <Box bg="brand.sidebar" borderWidth="1px" borderColor="brand.divider" borderRadius="12px" p={4}>
                                <Heading as="h3" size="sm" color="brand.navy" mb={2}>
                                    {t('demo.report.section.limits')}
                                </Heading>
                                <Text fontSize="sm" color="brand.ink">{t('demo.report.limits.body')}</Text>
                            </Box>

                            <Text fontSize="xs" color="brand.subtle">{t('demo.report.footer')}</Text>
                        </VStack>
                    </Box>
                </VStack>
            </Container>

            {/* Trial invitation */}
            <Box className="demo-no-print" bg="brand.quantumBg" borderTopWidth="1px" borderColor="brand.quantumBorder">
                <Container maxW="container.xl" py={{ base: 10, md: 14 }}>
                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} alignItems="center">
                        <Box>
                            <Heading as="h2" color="white" fontSize={{ base: '26px', md: '34px' }}>
                                {t('demo.cta.title')}
                            </Heading>
                            <Text mt={3} color="brand.quantumMuted">{t('demo.cta.body')}</Text>
                            <Text mt={3} color="brand.quantumSubtle" fontSize="sm">{t('demo.cta.note')}</Text>
                        </Box>
                        <VStack align={{ base: 'stretch', lg: 'flex-end' }} spacing={3}>
                            <Button
                                as={RouterLink}
                                to={trialHref}
                                size="lg"
                                rightIcon={<FiArrowRight />}
                                bgGradient="linear(135deg, brand.quantumAccent, brand.quantumCyan)"
                                color="brand.quantumBg"
                                _hover={{ opacity: 0.9 }}
                            >
                                {t('demo.cta.primary')}
                            </Button>
                            <Button
                                as="a"
                                href={demoTrialSampleAssetPath}
                                download
                                size="md"
                                variant="outline"
                                leftIcon={<FiDownload />}
                                color="brand.quantumAccent"
                                borderColor="rgba(0, 212, 170, 0.4)"
                                _hover={{ bg: 'rgba(0, 212, 170, 0.1)' }}
                            >
                                {t('demo.cta.secondary')}
                            </Button>
                        </VStack>
                    </SimpleGrid>
                </Container>
            </Box>

            <Box className="demo-no-print">
                <Footer />
            </Box>
        </Box>
    );
};

export default DemoPage;
